// Reglas de LINEUPS de ASOPROF (ingreso, sustitución y presencia).
// Movidas sin cambios de lógica desde EventINoOUT.jsx: cada validación devuelve
// { ok: true } o { ok: false, message }, y el componente muestra el mensaje.

// Se conservan los `==` originales: el backend puede enviar códigos numéricos como texto.
/* eslint-disable eqeqeq */

import { maxStatus, minStatus } from "./composition";

const OK = { ok: true };
const fail = (message) => ({ ok: false, message });

// Banda de carnet de un jugador en cancha (misma clasificación de las validaciones).
const bandOf = (player) => {
  if (player.edad >= 50 && player.positioncode !== 1) return "too_old";
  if (player.edad >= 45 && player.positioncode !== 1) return "old_more";
  if (player.edad >= 40 && player.positioncode !== 1) return "old";
  if (player.edad >= 35 && player.positioncode !== 1) return "middle";
  if (player.edad >= 32 && player.positioncode !== 1) return "young";
  if (Number(player.positioncode) === 1) return "goalkeeper";
  return null;
};

const playerTag = (player) =>
  player.positioncode === 1
    ? "(G)(AL)"
    : player.occupationcode === 1
    ? "(P)"
    : "(NP)";

const asoprofRules = {
  // Texto del interruptor que marca presencia sin ingresar al campo.
  presenceLabel: "Marcar presencia (No Juega)",

  // Etiqueta junto al nombre del jugador en el popup de ingreso y en PlayerRowBig.
  playerTag,
  playerRowTag: playerTag,

  // Tipo de jugador que muestra el tablero del delegado.
  playerTypeLabel: (player) =>
    Number(player.positioncode) === 1
      ? "Arquero"
      : Number(player.occupationcode) === 1
      ? "Profesional"
      : "No profesional",

  // Panel de composición del tablero del delegado: cantidad por carnet, carnets
  // en alerta y estado de cada regla de campo.
  composition: (inField) => {
    const bands = {
      goalkeeper: 0,
      young: 0,
      middle: 0,
      old: 0,
      old_more: 0,
      too_old: 0,
    };
    let noProfesional = 0;
    inField.forEach((player) => {
      const band = bandOf(player);
      if (band) bands[band]++;
      if (Number(player.occupationcode) === 217 && player.positioncode !== 1) {
        noProfesional++;
      }
    });

    const n = inField.length;
    const over45 = bands.old_more + bands.too_old;
    const over40 = bands.old + over45;
    const under40 = bands.young + bands.middle;

    return {
      bands,
      warn: {
        goalkeeper: bands.goalkeeper > 1,
        young: bands.young > 1,
        too_old: n >= 10 && bands.too_old === 0,
      },
      rules: [
        { name: "50+", value: `${bands.too_old}/1 mín.`, status: minStatus(bands.too_old, 1, n, 10) },
        { name: "45+", value: `${over45}/2 mín.`, status: minStatus(over45, 2, n, 9) },
        { name: "40+", value: `${over40}/4 mín.`, status: minStatus(over40, 4, n, 10) },
        { name: "Menores de 40", value: `${under40}/4 máx.`, status: maxStatus(under40, 4) },
        { name: "No profesionales", value: `${noProfesional}/4 máx.`, status: maxStatus(noProfesional, 4) },
        { name: "Arquero", value: `${bands.goalkeeper}/1 máx.`, status: maxStatus(bands.goalkeeper, 1) },
        { name: "Amarillos", value: `${bands.young}/1 máx.`, status: maxStatus(bands.young, 1) },
      ],
    };
  },

  // El backend devuelve `datas.remaining_suspension_count` cuando hay sanción activa.
  showSanctionMessage: true,

  // Una sanción activa impide ingresar (sin opción de confirmar).
  blockActiveSanction: true,

  // Bug conocido y conservado: al confirmar el aviso de regla (ingreso o sustitución),
  // se registra sin volver a revisar tarjetas ni sanciones.
  blockSanctionedOnConfirm: false,

  // Un jugador ya no puede volver a cambiarse si tuvo carnet young/middle/old.
  canSubstitute: ({ dataArr, usercode }) => {
    const userElements = dataArr.filter(
      (item) => item.usercode === usercode && item.eventcode !== 15
    );
    const youngPresent = userElements.some(
      (item) =>
        item.media === "young" || item.media === "middle" || item.media === "old"
    );
    return !youngPresent;
  },

  // Ingreso al campo, o presencia (isAttendance) sin repetirla.
  validateEntry: ({ inField, userIn, dataArr, isAttendance }) => {
    if (isAttendance) {
      const userElements = dataArr.filter(
        (item) => item.usercode === userIn.usercode && item.eventcode === 15
      );

      if (userElements.length === 0) {
        return OK;
      }
      return fail(
        "No se permite registrar más de una vez la asistencia de un mismo jugador."
      );
    }

    let countOld = 0;
    let countTooOld = 0;
    let countOldMore = 0;
    let countYoung = 0;
    let countMiddle = 0;
    let countGoal = 0;
    let countNoProfesional = 0;

    for (let i = 0; i < inField.length; i++) {
      const player = inField[i];
      if (player.edad >= 50 && player.positioncode !== 1) {
        countTooOld++;
      } else if (player.edad >= 45 && player.positioncode !== 1) {
        countOldMore++;
      } else if (player.edad >= 40 && player.positioncode !== 1) {
        countOld++;
      } else if (player.edad >= 35 && player.positioncode !== 1) {
        countMiddle++;
      } else if (player.edad >= 32 && player.positioncode !== 1) {
        countYoung++;
      } else if (player.positioncode == 1) {
        countGoal++;
      }
      if (player.occupationcode == 217 && player.positioncode !== 1) {
        countNoProfesional++;
      }
    }

    if (userIn.edad >= 50 && userIn.positioncode !== 1) {
      countTooOld++;
    } else if (userIn.edad >= 45 && userIn.positioncode !== 1) {
      countOldMore++;
    } else if (userIn.edad >= 40 && userIn.positioncode !== 1) {
      countOld++;
    } else if (userIn.edad >= 35 && userIn.positioncode !== 1) {
      countMiddle++;
    } else if (userIn.edad >= 32 && userIn.positioncode !== 1) {
      countYoung++;
    } else if (userIn.positioncode == 1) {
      countGoal++;
    }
    if (userIn.occupationcode == 217 && userIn.positioncode !== 1) {
      countNoProfesional++;
    }

    // REGLA DE LOS NO PROFESIONALES (SE OMITE EL ARQUERO EN ESA REGLA)
    if (countNoProfesional > 4) {
      return fail(
        "No se permite el ingreso ya que quedarian en campo " +
          5 +
          " NO PROFESIONALES. (MÁXIMO 4 NO PROFESIONALES)"
      );
    }
    // REGLA DE MAXIMO UN MENOR
    if (countYoung > 1) {
      return fail(
        "No se permite el ingreso ya que quedarian en campo " +
          countYoung +
          " carnet AMARILLO. (MÁXIMO 1 MENOR DE 35 AÑOS)"
      );
    }
    // UN PORTERO POR EQUIPO
    if (countGoal > 1) {
      return fail(
        "No se permite el ingreso ya que quedarian en campo 2 carnet NEGROS (MAXIMO 1 PORTERO)."
      );
    }
    // REGLA QUE OBLIGA A 2 MAYORES DE 45 EN CAMPO
    if (
      inField.length === 9 &&
      countOldMore === 0 &&
      countTooOld == 0 &&
      userIn.edad < 45
    ) {
      return fail(
        "No se permite el ingreso, ya que no se completaria la cantidad suficiente de CARNET AZUL y CARNET PLATEADO. (ES OBLIGATORIO 2 MAYORES DE 45)"
      );
    }
    // REGLA QUE OBLIGA AL MENOS A 1 MAYOR DE 50 EN CAMPO
    if (inField.length === 10 && countTooOld === 0 && userIn.edad < 50) {
      return fail(
        "No se permite el ingreso, ya que no se completaria la cantidad suficiente de CARNET PLATEADO. (OBLIGATORIO 1 MAYOR de 50)"
      );
    }
    // REGLA QUE OBLIGA A 2 MAYORES DE 45 EN CAMPO
    if (inField.length === 10 && countOldMore === 0 && userIn.edad < 45) {
      return fail(
        "No se permite el ingreso, ya que no se completaria la cantidad suficiente de CARNET AZUL o PLATEADOS. (ES OBLIGATORIO 2 MAYORES DE 45)"
      );
    }
    // REGLA DE MINIMO 4 JUGADORES MAYORES DE 40
    if (inField.length === 10 && countOld + countTooOld + countOldMore < 4) {
      return fail(
        "No se permite el ingreso, ya que no se completaria la cantidad suficiente de CARNET ROJO + CARNET AZUL + CARNET PLATEADOS. (MÍNIMO 4 JUGADORES DE 40 EN ADELANTE)"
      );
    }
    // REGLA DE MÁXIMO 4 JUGADORES MENORES DE 40
    if (countYoung + countMiddle > 4) {
      return fail(
        "No se permite el ingreso, ya que se completaria la cantidad MÁXIMA de MENORES (MÁXIMO 4 JUGADORES MENORES DE 40)"
      );
    }

    return OK;
  },

  // Sustitución: sale userOut, entra userIn.
  validateSubstitution: ({ inField, userOut, userIn }) => {
    let countOld = 0;
    let countTooOld = 0;
    let countYoung = 0;
    let countMiddle = 0;
    let countGoal = 0;
    let countOldMore = 0;
    let countNoProfesional = 0;

    for (let i = 0; i < inField.length; i++) {
      const player = inField[i];
      // El jugador que sale no cuenta
      if (player.usercode === userOut.usercode) {
        continue;
      }
      if (player.edad >= 50 && player.positioncode !== 1) {
        countTooOld++;
      } else if (player.edad >= 45 && player.positioncode !== 1) {
        countOldMore++;
      } else if (player.edad >= 40 && player.positioncode !== 1) {
        countOld++;
      } else if (player.edad >= 35 && player.positioncode !== 1) {
        countMiddle++;
      } else if (player.edad >= 32 && player.positioncode !== 1) {
        countYoung++;
      } else if (player.positioncode == 1) {
        countGoal++;
      }
      if (player.occupationcode == 217 && player.positioncode !== 1) {
        countNoProfesional++;
      }
    }

    if (userIn.edad >= 50 && userIn.positioncode !== 1) {
      countTooOld++;
    } else if (userIn.edad >= 45 && userIn.positioncode !== 1) {
      countOldMore++;
    } else if (userIn.edad >= 40 && userIn.positioncode !== 1) {
      countOld++;
    } else if (userIn.edad >= 35 && userIn.positioncode !== 1) {
      countMiddle++;
    } else if (userIn.edad >= 32 && userIn.positioncode !== 1) {
      countYoung++;
    } else if (userIn.positioncode == 1) {
      countGoal++;
    }
    if (userIn.occupationcode == 217 && userIn.positioncode !== 1) {
      countNoProfesional++;
    }

    // REGLA DE LOS NO PROFESIONALES (SE OMITE EL ARQUERO EN ESA REGLA)
    if (countNoProfesional > 4) {
      return fail(
        "No se permite el ingreso ya que quedarian en campo " +
          5 +
          " NO PROFESIONALES. (MÁXIMO 4 NO PROFESIONALES)"
      );
    }
    // REGLA DE MAXIMO UN MENOR
    if (countYoung > 1) {
      return fail(
        "No se permite el ingreso ya que quedarian en campo " +
          countYoung +
          " carnet AMARILLO. (MÁXIMO 1 MENOR DE 35 AÑOS)"
      );
    }
    // UN PORTERO POR EQUIPO
    if (countGoal > 1) {
      return fail(
        "No se permite LA SUSTITUCIÓN ya que quedarian en campo 2 carnet NEGROS (MAXIMO 1 PORTERO)."
      );
    }
    // REGLA QUE OBLIGA A 2 MAYORES DE 45 EN CAMPO
    if (
      inField.length > 8 &&
      countOldMore + countTooOld < 2 &&
      userIn.edad < 45
    ) {
      return fail(
        "No se permite LA SUSTITUCIÓN, ya que no se completaria la cantidad suficiente de CARNET AZUL y CARNET PLATEADO. (ES OBLIGATORIO 2 MAYORES DE 45)"
      );
    }
    // REGLA QUE OBLIGA AL MENOS A 1 MAYOR DE 50 EN CAMPO
    if (inField.length > 10 && countTooOld === 0 && userIn.edad < 50) {
      return fail(
        "No se permite LA SUSTITUCIÓN, ya que no se completaria la cantidad suficiente de CARNET PLATEADO. (OBLIGATORIO 1 MAYOR de 50)"
      );
    }
    // REGLA QUE OBLIGA A 2 MAYORES DE 45 EN CAMPO
    if (inField.length === 10 && countOldMore === 0 && userIn.edad < 45) {
      return fail(
        "No se permite LA SUSTITUCIÓN, ya que no se completaria la cantidad suficiente de CARNET AZUL o PLATEADOS. (ES OBLIGATORIO 2 MAYORES DE 45)"
      );
    }
    // REGLA DE MINIMO 4 JUGADORES MAYORES DE 40
    if (inField.length === 10 && countOld + countTooOld + countOldMore < 4) {
      return fail(
        "No se permite LA SUSTITUCIÓN, ya que no se completaria la cantidad suficiente de CARNET ROJO + CARNET AZUL + CARNET PLATEADOS. (MÍNIMO 4 JUGADORES DE 40 EN ADELANTE)"
      );
    }
    // REGLA DE MÁXIMO 4 JUGADORES MENORES DE 40
    if (countYoung + countMiddle > 4) {
      return fail(
        "No se permite LA SUSTITUCIÓN, ya que se completaria la cantidad MÁXIMA de MENORES (MÁXIMO 4 JUGADORES MENORES DE 40)"
      );
    }

    return OK;
  },
};

export default asoprofRules;
